/**
 * Kenya county map: one marker per county (GADM centroid). Tap opens detail modal (census + Add to list),
 * same two-step flow as US ZCTA collection map.
 */
import React, { useCallback, useMemo, useState } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import { getMapTilerStreetTileUrl } from "../../utils/mapTilerStreetTileUrl";
import { Box, Typography } from "@mui/material";
import { GTG_KENYA_COUNTIES } from "@healthdesk/shared-utils";
import "leaflet/dist/leaflet.css";
import KenyaCountyMapMarker from "./KenyaCountyMapMarker";
import KenyaCountyDetailModal from "./KenyaCountyDetailModal";
import {
  getKenyaCountyCentroid,
  getKenyaCountyGoogleAdsDisplayName,
  KENYA_COUNTY_CENTROID_SOURCE_NOTE,
  KENYA_GOOGLE_ADS_COUNTY_LABEL_NOTE,
} from "../../services/census/getKenyaCountyCensusSnapshot";

const KENYA_CENTER = { lat: 0.69, lng: 37.95 };
const DEFAULT_ZOOM = 6;

const KenyaCountyMapPicker = ({
  selectedCounties,
  onCountyToggle,
  disabled = false,
  height = 380,
}) => {
  const [detailCounty, setDetailCounty] = useState(null);

  const handleOpenDetail = useCallback((canon) => {
    setDetailCounty(typeof canon === "string" ? canon : "");
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailCounty(null);
  }, []);

  const markers = useMemo(
    () =>
      GTG_KENYA_COUNTIES.map((canon) => {
        const pos = getKenyaCountyCentroid(canon);
        if (!pos) return null;
        return {
          county: canon,
          displayName: getKenyaCountyGoogleAdsDisplayName(canon),
          lat: pos.lat,
          lng: pos.lng,
        };
      }).filter(Boolean),
    []
  );

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        <strong>Tap a marker</strong> to open census details and use <strong>Add to list</strong> (two steps,
        same as the US map). Markers use approximate county centers (for placement only).{" "}
        {KENYA_GOOGLE_ADS_COUNTY_LABEL_NOTE} {KENYA_COUNTY_CENTROID_SOURCE_NOTE}
      </Typography>
      <Box sx={{ height, width: "100%", position: "relative" }}>
        <MapContainer
          center={[KENYA_CENTER.lat, KENYA_CENTER.lng]}
          zoom={DEFAULT_ZOOM}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            url={getMapTilerStreetTileUrl()}
            attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>'
          />
          {markers.map((m) => (
            <KenyaCountyMapMarker
              key={m.county}
              county={m.county}
              displayName={m.displayName}
              lat={m.lat}
              lng={m.lng}
              isSelected={selectedCounties.has(m.county)}
              onOpenDetail={handleOpenDetail}
              disabled={disabled}
            />
          ))}
        </MapContainer>
      </Box>

      <KenyaCountyDetailModal
        open={Boolean(detailCounty)}
        county={detailCounty}
        isSelected={detailCounty ? selectedCounties.has(detailCounty) : false}
        onClose={handleCloseDetail}
        onAddOrRemove={onCountyToggle}
      />
    </Box>
  );
};

export default KenyaCountyMapPicker;
